/*  
    Developers: Sakshi Bhise  
    File: Chat.tsx  
    Date: 04/18/2026  
    Project Name: AskHR  
    Team: DSET  
  
    Purpose:  
    This file creates the main Chat component. It handles everything about the chat experience:  
      - Managing chat messages  
      - Streaming responses from the assistant  
      - Keeping chat history  
      - Handling tickets and attachments  
      - Showing citations and FAQ dialogs  
      - Handling errors and supporting different languages  
    It talks to the back-end (the server) and keeps the app feeling lively and easy for the user.  
*/

//--------------------------- Imports ---------------------------//  
// Imagine these like gathering all your gardening tools before starting a project!  
import { useRef, useState, useEffect, useCallback, useContext, useLayoutEffect, Fragment } from "react"; // React helpers for managing state, events, and context.
import { motion, AnimatePresence, LayoutGroup } from "motion/react";
import { IconButton, IIconProps, useTheme } from "@fluentui/react"; // UI components for dialogs, buttons, spinners, and layout.  
import uuid from 'react-uuid'; // Used to give unique IDs to messages and objects.  
import { isEmpty } from "lodash-es"; // Helper to check if something (like a list) is empty. 
import * as teams from "@microsoft/teams-js"; // Microsoft Teams SDK for Teams authentication 
import {
    // These are like the gardening “recipes” – functions and data types you use to run the chat.  
    ChatMessage,
    ConversationRequest,
    Citation,
    ToolMessageContent,
    ChatResponse,
    historyGenerate,
    historyUpdate,
    sendEmail,
    logEvent,
    generateDescription,
} from "../../api";
import { AppStateContext } from "../../state/AppProvider"; // Global app state, like the soil for your plants.
import { useBoolean } from "@fluentui/react-hooks"; // Helper to easily manage true/false values.
import { useTranslation } from "react-i18next"; // Helper for translating text into different languages.
import { useOutletContext } from "react-router-dom"; // Lets you get context from parent layout.
import { OutletContext } from "../../components/Sidebar/SidebarLayout";
import { toast } from "react-toastify"; // Shows little pop-up messages (“toasts”).  
import { AuthContext } from "../../state/AuthContext"; // Authentication info.  
import { Dialog, MessageBubble, SplitOmnibox, SubmitTicketPayload, TicketSuggestion } from "@teva/ui";
import TevaLogo from "../../assets/TevaPharm.svg"
import { getStyles } from "./Chat.styles";
import { Answer } from "../../components/Answer";
import { Ticket } from "@teva/ui"
import i18next from "i18next";
import styles from "./Chat.module.css";

const CANCEL_ICON: IIconProps = { iconName: 'Cancel' };


//--------------------------- Enums and Styles ---------------------------//  

// Enum for message streaming status  
const enum messageStatus {
    NotRunning = "Not Running",
    Processing = "Processing",
    Done = "Done"
}

// Dialog box styles  
const dialogStyles = {
    main: {
        width: '50% !important', // Dialog takes up half the width of the page.  
        maxHeight: '86% !important',
        maxWidth: '787px !important',
    },
};

//--------------------------- Main Chat Component ---------------------------//  
// This is like your garden bed where all the plants (features) grow!  
const Chat = () => {
    // --- Contexts and State Setup ---  
    // Get the global app state (like the current season in your garden)  
    const appStateContext = useContext(AppStateContext);

    // Get the current authentication token (like the key to the garden gate)  
    const { token }: any = useContext(AuthContext);

    // Set up translation for different languages  
    const { t } = useTranslation(); // `t` lets you translate words  

    const theme = useTheme();

    // References and states for scroll, loading, messages, etc.
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null); // To scroll to the end of messages
    const [isLoading, setIsLoading] = useState<boolean>(false); // Are we currently loading/generating a response?
    const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false); // Show animated loading dots
    const abortFuncs = useRef([] as AbortController[]); // Used to stop API requests if needed
    const [messages, setMessages] = useState<ChatMessage[]>([])

    const stylesCustom = getStyles(theme, messages.length > 0) // All messages in the chat  
    const [processMessages, setProcessMessages] = useState<messageStatus>(messageStatus.NotRunning); // Status of message processing  
    // const [clearingChat, setClearingChat] = useState<boolean>(false); // Are we clearing the chat?  
    const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true); // Show/hide error dialog  
    // const [errorMsg, setErrorMsg] = useState<ErrorMessage | null>() // Store error messages for dialogs  
    const [questionInput, setQuestionInput] = useState(""); // The user’s current question input
    const userName = appStateContext?.state.loggedInUserName?.firstName;
    const [ASSISTANT, TOOL, ERROR] = ["assistant", "tool", "error"] // Message roles  
    const [ticket, setTicket] = useState({
        Title: "", // The ticket’s title/category  
        Description: "",
        Attachment: null,
    });
    const [isTicketDialogVisible, setIsTicketDialogVisible] = useState(false); // Show/hide ticket dialog  
    const [isSubmitting, setIsSubmitting] = useState(false); // Are we submitting a ticket?  
    const [ticketDialogType, setTicketDialogType] = useState<'form' | 'success' | 'error'>('form'); // Which view in ticket dialog  
    // const [ticketDialogMessage, setTicketDialogMessage] = useState(''); // Message to show after ticket submission  
    const { newChatRef, openTicketRef } = useOutletContext<OutletContext>(); // References for starting a new chat and opening the ticket dialog
    const country = appStateContext?.state.loggedInUserCountry ? appStateContext?.state.loggedInUserCountry : ''; // User’s country  
    const imUserCountry = appStateContext?.state.impUserCountry ? appStateContext?.state.impUserCountry : ''; // User’s country  
    const imUser = appStateContext?.state.ImpersonatedUser?.split('.')[0];
    const imUsername = imUser ? imUser.charAt(0).toUpperCase() + imUser.slice(1) : null;
    const appName = appStateContext?.state.frontendSettings?.ui?.title ?? "AskHR"
    const [openDisclaimer, setOpenDisclaimer] = useState(false);

    // --- Effects (Like watering and sun for your plants) ---  
    useEffect(() => {
        // Register new chat reference for other parts of the app
        if (newChatRef) {
            newChatRef.current = newChat;
            return () => { newChatRef.current = null; }
        }
    }, [newChatRef]);

    // useEffect(() => {
    //     // Show error if chat history DB is not working  
    //     if (
    //         appStateContext?.state.isSqlDBAvailable?.status !== SQLDBStatus.Working &&
    //         appStateContext?.state.isSqlDBAvailable?.status !== SQLDBStatus.NotConfigured &&
    //         appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail &&
    //         hideErrorDialog
    //     ) {
    //         let subtitle = `${appStateContext.state.isSqlDBAvailable.status}. Please contact the site administrator.`
    //         setErrorMsg({
    //             title: "Chat history is not enabled",
    //             subtitle: subtitle
    //         })
    //         toggleErrorDialog();
    //     }
    // }, [appStateContext?.state.isSqlDBAvailable]);

    // --- Ticket Handling Functions ---  
    const openTicketDialog = async () => {
        // Show the ticket dialog for submitting issues  
        setIsTicketDialogVisible(true);
        // Log that the ticket button was clicked  
        logEvent({ "eventType": "HR Ticket Button Click", "conversationId": appStateContext?.state.currentChat?.id }, country, appStateContext?.state?.userGId, token, imUserCountry);
        if (appStateContext?.state.currentChat?.messages) {
            setIsLoading(true);
            try {
                // Ask the back-end to generate a ticket description from chat messages  
                const response = await generateDescription(
                    token,
                    appStateContext?.state.currentChat?.messages,
                    appStateContext.state.loggedInUserCountry,
                    appStateContext.state.userGId,
                    appStateContext.state.impUserCountry)
                const jsonResponse = await response.json();
                // Fill the ticket info with the response  
                setTicket({
                    Title: jsonResponse.title ? jsonResponse.title : "",
                    Description: jsonResponse.description,
                    Attachment: null,
                });
            }
            catch (error) {
                // Handle errors if needed (no action here)  
            } finally {
                setIsLoading(false); // Stop loader whether success or error  
            }
        }
    }

    // Always keep ref pointed at the latest closure so sidebar nav never calls a stale version
    if (openTicketRef) {
        openTicketRef.current = openTicketDialog;
    }

    const closeTicketDialog = () => {
        // Close the ticket dialog and reset its state  
        setIsTicketDialogVisible(false);
        setTicketDialogType('form');
    };

    const resetTicketToOriginal = () => {
        // Reset ticket fields to empty  
        setTicket({
            Title: "",
            Description: "",
            Attachment: null,
        });
    };

    const saveTicket = async (payload: SubmitTicketPayload) => {
        // Only submit if both fields are filled in  
        if (
            payload.title !== null && payload.title.trim() !== "" &&
            payload.description !== null && payload.description.trim() !== ""
        ) {
            setIsSubmitting(true); // Disable UI while submitting  
            // Log submission event  
            logEvent({ "eventType": "HR Ticket Submitted", "conversationId": appStateContext?.state.currentChat?.id }, country, appStateContext?.state?.userGId, token, imUserCountry);
            // Show loading toast  
            const id = toast.loading(<div style={{
                direction: isRTL(t("chat.prompt")) ? "rtl" : "ltr",
                textAlign: isRTL(t("layout.HowCanIHelpYouToday")) ? "right" : "left",
            }} >{t("ticketMenu.emailProgress")}</div>);
            // Send ticket info to the server  
            const response = await sendEmail(payload, appStateContext?.state.userGId, appStateContext?.state.loggedInUserCountry, token, appStateContext?.state.impUserCountry);

            if (response) {
                // If successful, show success message  
                toast.update(id, {
                    render: <div style={{
                        direction: isRTL(t("layout.HowCanIHelpYouToday")) ? "rtl" : "ltr",
                        textAlign: isRTL(t("layout.HowCanIHelpYouToday")) ? "right" : "left",
                    }} >{t("ticketMenu.emailSuccessToast")}</div>,
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                });
                setTicketDialogType('success');
                setIsTicketDialogVisible(true);
                resetTicketToOriginal();
            } else {
                // If failed, show error message  
                toast.update(id, {
                    render: <div style={{
                        direction: isRTL(t("layout.HowCanIHelpYouToday")) ? "rtl" : "ltr",
                        textAlign: isRTL(t("layout.HowCanIHelpYouToday")) ? "right" : "left",
                    }} >{t("ticketMenu.emailFailToast")}</div>,
                    type: "error",
                    isLoading: false,
                    autoClose: 5000,
                });
                setTicketDialogType('error');
                setIsTicketDialogVisible(true);
            }
            setIsSubmitting(false); // Re-enable UI  
        } else {
            // Warn if required fields are missing  
            toast.warning(<div style={{
                direction: isRTL(t("layout.HowCanIHelpYouToday")) ? "rtl" : "ltr",
                textAlign: isRTL(t("layout.HowCanIHelpYouToday")) ? "right" : "left",
            }} >{t("ticketMenu.requiredFields")}</div>);
        }
    };
    //--- Ticket Code End ---  

    // --- Message Processing Functions ---  
    let assistantMessage = {} as ChatMessage
    let toolMessage = {} as ChatMessage
    let assistantContent = ""

    // Function to process response messages (sorts and updates message lists)  
    const processResultMessage = (resultMessage: ChatMessage, userMessage: ChatMessage, conversationId?: string) => {
        if (resultMessage.role === ASSISTANT) {
            assistantContent += resultMessage.content
            assistantMessage = resultMessage
            assistantMessage.content = assistantContent
            if (resultMessage.context) {
                toolMessage = {
                    id: uuid(),
                    role: TOOL,
                    content: resultMessage.context,
                    date: new Date().toISOString(),
                }
            }
        }
        if (resultMessage.role === TOOL) toolMessage = resultMessage
        if (!conversationId) {
            isEmpty(toolMessage) ?
                setMessages([...messages, userMessage, assistantMessage]) :
                setMessages([...messages, userMessage, toolMessage, assistantMessage]);
        } else {
            isEmpty(toolMessage) ?
                setMessages([...messages, assistantMessage]) :
                setMessages([...messages, toolMessage, assistantMessage]);
        }
    }

    /**  
     * Detects if current device is a android tablet or iPad using user agent and touch points.
     * Returns false if device is not ipad or android tablet  
     */
    // Detects if the device is a tablet or iPad  
    function isTabletOrIpad() {
        const ua = navigator.userAgent;
        if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return true;
        if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true;
        if (/Macintosh/i.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) return true;
        return false;
    }

    // Detects if the device is a mobile or iPhone  
    function isMobileOrIphone() {
        const ua = navigator.userAgent;
        // iPhone, Android Mobiles, iPod, etc.  
        if (/iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|webOS|Opera Mini|IEMobile|Mobile/i.test(ua)) {
            return true;
        }
        return false;
    }

    // Detects if the device is a laptop or desktop  
    function isDesktopOrLaptop() {
        return !isMobileOrIphone() && !isTabletOrIpad();
    }

    function getDeviceType() {
        if (isTabletOrIpad()) return "Tablet/IPad";
        if (isMobileOrIphone()) return "Mobile/IPhone";
        return "Laptop/Desktop";
    }

    // ======================= Device Detection ======================= //  
    /**  
     * Checks if the app is running inside Microsoft Teams.  
     * Uses iframe detection, user agent, and Teams SDK with timeout.
     * Returns true if app is running on teams.
     */
    async function isRunningInTeams() {
        const isInIframe = window.self !== window.top;
        // navigator.userAgent is read only property in JavaScript used to identify device properties. 
        const isTeamsUA = /teams/.test(navigator.userAgent.toLowerCase());

        if (!isInIframe) return false; // Not in iframe, not in Teams  

        if (typeof teams === "undefined") return isTeamsUA; // No Teams SDK, fallback to User Agent detection 

        // Try to initialize Teams SDK with a timeout of 1500ms 
        function initializeTeamsWithTimeout(ms = 1500) {
            return Promise.race([
                teams.app.initialize(), // function to initialize teams sdk 
                new Promise((_, reject) => setTimeout(() => reject(new Error("Teams init timeout")), ms))
            ]);
        }

        try {
            await initializeTeamsWithTimeout();
            const context = await teams.app.getContext();  // Retrieves the current context the frame is running in.
            return !!context;
        } catch {
            // Teams SDK failed or timed out, fallback to User Agent detection   
            return isTeamsUA;
        }
    }

    // Convert to Python-like timedelta string
    function formatPythonTimedelta(durationMs: any) {
        const totalMicroseconds = durationMs * 1000;

        const hours = Math.floor(totalMicroseconds / (3600 * 1e6));
        const minutes = Math.floor((totalMicroseconds % (3600 * 1e6)) / (60 * 1e6));
        const seconds = Math.floor((totalMicroseconds % (60 * 1e6)) / 1e6);
        const microseconds = totalMicroseconds % 1e6;

        return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(microseconds).padStart(6, "0")}`;
    }

    // Function to create chat API request when chat history is enabled  
    const makeApiRequestWithSqlDB = async (question: string, conversationId?: string) => {
        setIsLoading(true);
        setShowLoadingMessage(true);
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        // Count words and characters in the question
        const wordCount = countWords(question);
        const charCount = question.trim().length;
        const hasCJK = /[一-鿿぀-ヿ가-힯]/.test(question);
        // If the prompt is too short (like only "hi"), ask the user to elaborate
        if (!conversationId && !hasCJK && (wordCount < 4 || charCount < 15)) {
            const userMessage: ChatMessage = {
                id: uuid(),
                role: "user",
                content: question,
                date: new Date().toISOString(),
            };
            const elaborateMessage: ChatMessage = {
                id: uuid(),
                role: "assistant",
                content: t("chat.promptLimitText"),
                date: new Date().toISOString(),
            };
            setMessages([...messages, userMessage, elaborateMessage]);
            setIsLoading(false);
            setShowLoadingMessage(false);
            return;
        }

        const userMessage: ChatMessage = {
            id: uuid(),
            role: "user",
            content: question,
            date: new Date().toISOString(),
        };

        // Set up API request data  
        let request: ConversationRequest;
        let conversation;
        if (conversationId) {
            conversation = appStateContext?.state?.chatHistory?.find((conv) => conv.id === conversationId)
            if (!conversation) {
                setIsLoading(false);
                setShowLoadingMessage(false);
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                return;
            } else {
                conversation.messages.push(userMessage);
                request = {
                    messages: [...conversation.messages.filter((answer) => answer.role !== ERROR)],
                    user_permission: appStateContext?.state.userPermission,
                };
            }
        } else {
            request = {
                messages: [userMessage].filter((answer) => answer.role !== ERROR),
                user_permission: appStateContext?.state.userPermission,
            };
            setMessages(request.messages)
        }
        let result = {} as ChatResponse;
        try {
            // 1. Record the start time as a Date object  
            const startDate = new Date();
            const startTime = startDate.getTime();
            const startDatetime = startDate.toISOString();


            const response = conversationId ? await historyGenerate(request, abortController.signal, country, token, appStateContext?.state?.ImpersonatedUser, appStateContext?.state.userGId, conversationId, imUserCountry) : await historyGenerate(request, abortController.signal, country, token, appStateContext?.state?.ImpersonatedUser, appStateContext?.state.userGId, undefined, imUserCountry);
            // 3. When response is received, record end time as a Date object  
            const endDate = new Date();
            const endTime = endDate.getTime();
            const endDatetime = endDate.toISOString();

            // 4. Calculate duration (optional)  
            const duration = formatPythonTimedelta(endTime - startTime);

            console.log("Request duration (microseconds):", duration);

            // 5. Send another event with end_datetime and duration  
            await logEvent({ "eventType": "Prompt performance and device details", "device": getDeviceType(), "browser": await isRunningInTeams() ? "App Running on Teams" : "App Running on Web Browser", "screenWidth": window.innerWidth, "screenHeight": window.innerHeight, "start_datetime": startDatetime, "end_datetime": endDatetime, "duration": duration }, country, appStateContext?.state.userGId, token, imUserCountry);

            if (!response?.ok) {
                // If the response is an error, show error message in chat  
                const responseJson = await response.json();
                var errorResponseMessage = responseJson.error === undefined ? "Please try again. If the problem persists, please contact the site administrator." : responseJson.error;
                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: `Oops, something went wrong, please try again.`,
                    date: new Date().toISOString()
                }
                let resultConversation;
                if (conversationId) {
                    resultConversation = appStateContext?.state?.chatHistory?.find((conv) => conv.id === conversationId)
                    if (!resultConversation) {
                        setIsLoading(false);
                        setShowLoadingMessage(false);
                        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                        return;
                    }
                    resultConversation.messages.push(errorChatMsg);
                } else {
                    setMessages([...messages, userMessage, errorChatMsg])
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                }
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation });
                setMessages([...resultConversation.messages]);
                return;
            }
            if (response?.body) {
                // If the response is a stream, read it chunk by chunk (like picking tomatoes one by one)  
                const reader = response.body.getReader();
                let runningText = "";
                while (true) {
                    setProcessMessages(messageStatus.Processing)
                    const { done, value } = await reader.read();
                    if (done) break;
                    var text = new TextDecoder("utf-8").decode(value);
                    const objects = text.split("\n");
                    objects.forEach((obj) => {
                        try {
                            if (obj !== "" && obj !== "{}") {
                                runningText += obj;
                                result = JSON.parse(runningText);
                                if (result.choices?.length > 0) {
                                    result.choices[0].messages.forEach((msg) => {
                                        msg.id = msg.role === ASSISTANT ? result.id : uuid();
                                        msg.date = new Date().toISOString();
                                    })
                                    if (result.choices[0].messages?.some(m => m.role === ASSISTANT)) {
                                        setShowLoadingMessage(false);
                                    }
                                    result.choices[0].messages.forEach((resultObj) => {
                                        processResultMessage(resultObj, userMessage, conversationId);
                                    })
                                }
                                runningText = "";
                            }
                            else if (result.error) {
                                throw Error(result.error);
                            }
                        }
                        catch (e) {
                            if (!(e instanceof SyntaxError)) {
                                throw e;
                            } else {
                                // Incomplete message, wait for the next chunk  
                            }
                        }
                    });
                }
                let resultConversation;
                if (conversationId) {
                    resultConversation = appStateContext?.state?.chatHistory?.find((conv) => conv.id === conversationId)
                    if (!resultConversation) {
                        setIsLoading(false);
                        setShowLoadingMessage(false);
                        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                        return;
                    }
                    isEmpty(toolMessage) ?
                        resultConversation.messages.push(assistantMessage) :
                        resultConversation.messages.push(toolMessage, assistantMessage)
                } else {
                    resultConversation = {
                        id: result.history_metadata.conversation_id,
                        title: result.history_metadata.title,
                        messages: [userMessage],
                        date: result.history_metadata.date
                    }
                    isEmpty(toolMessage) ?
                        resultConversation.messages.push(assistantMessage) :
                        resultConversation.messages.push(toolMessage, assistantMessage)
                }
                if (!resultConversation) {
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                }
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation });
                isEmpty(toolMessage) ?
                    setMessages([...messages, assistantMessage]) :
                    setMessages([...messages, toolMessage, assistantMessage]);
            }
        } catch (e) {
            // If something goes wrong, show error message in chat  
            if (!abortController.signal.aborted) {
                let errorMessage = `Oops, something went wrong, please try again.`;
                if (result.error?.message) {
                    errorMessage = result.error.message;
                }
                else if (typeof result.error === "string") {
                    errorMessage = result.error;
                }
                let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString()
                }
                let resultConversation;
                if (conversationId) {
                    resultConversation = appStateContext?.state?.chatHistory?.find((conv) => conv.id === conversationId)
                    if (!resultConversation) {
                        setIsLoading(false);
                        setShowLoadingMessage(false);
                        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                        return;
                    }
                    resultConversation.messages.push(errorChatMsg);
                } else {
                    if (!result.history_metadata) {
                        let errorChatMsg: ChatMessage = {
                            id: uuid(),
                            role: ERROR,
                            content: errorMessage,
                            date: new Date().toISOString()
                        }
                        setMessages([...messages, userMessage, errorChatMsg])
                        setIsLoading(false);
                        setShowLoadingMessage(false);
                        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                        return;
                    }
                    resultConversation = {
                        id: result.history_metadata.conversation_id,
                        title: result.history_metadata.title,
                        messages: [userMessage],
                        date: result.history_metadata.date
                    }
                    resultConversation.messages.push(errorChatMsg);
                }
                if (!resultConversation) {
                    setIsLoading(false);
                    setShowLoadingMessage(false);
                    abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
                    return;
                }
                appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: resultConversation });
                setMessages([...messages, errorChatMsg]);
            } else {
                setMessages([...messages, userMessage])
            }
        } finally {
            setIsLoading(false);
            setShowLoadingMessage(false);
            abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            setTimeout(() => setProcessMessages(messageStatus.Done), 100);
        }
        return abortController.abort();
    }

    // Utility to detect if the text is in a right-to-left language
    const isRTL = (text: string): boolean => {
        const rtlCharRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/; // Hebrew, Arabic, etc.
        return rtlCharRegex.test(text);
    };

    const isRtlLang = i18next.language === 'he' || i18next.language === 'ar';

    // Function to handle retry prompt  
    const handleRetry = (userPrompt: string) => {
        if (!userPrompt) {
            return;
        }
        handleSendQuestion(userPrompt, appStateContext?.state.currentChat?.id);
    };

    // Converts an ISO date string into a Date object, forcing UTC parsing.
    // Safari (JavaScriptCore) treats ISO strings without a timezone as local time,
    // while Chrome/V8 treats them as UTC — appending Z avoids that inconsistency.
    const parseTimestampToDate = (timestamp?: string): Date | undefined => {
        if (!timestamp) return undefined;
        let normalized = timestamp.trim().replace(" ", "T");
        if (!/Z$|[+-]\d{2}:?\d{2}$/.test(normalized)) {
            normalized += "Z";
        }
        const parsed = new Date(normalized);
        return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    };

    // Function to start a new chat  
    const newChat = () => {
        setProcessMessages(messageStatus.Processing)
        setMessages([])
        abortFuncs.current.forEach(a => a.abort());
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: null });
        setQuestionInput("");
        setProcessMessages(messageStatus.Done);
    };

    // Helper function to count words in a string  
    // word counter: handles Chinese + English correctly
    function countWords(str: string): number {
        const text = str.trim();

        if (!text) return 0;

        // Detect any CJK characters (Chinese / Japanese / Korean)
        const cjkRegex = /[\u4E00-\u9FFF]/;

        if (cjkRegex.test(text)) {
            // Count each CJK character as a word
            return text.length;
        }

        // Default: split by whitespace
        return text.split(/\s+/).filter(Boolean).length;
    }

    // Effect: Update messages when the current chat changes  
    useEffect(() => {
        if (appStateContext?.state.currentChat) {
            setMessages(appStateContext.state.currentChat.messages)
        } else {
            setMessages([])
        }
    }, [appStateContext?.state.currentChat]);

    // Effect: Save messages to DB when chat finishes processing  
    useLayoutEffect(() => {
        const saveToDB = async (messages: ChatMessage[], id: string, title: string) => {
            const response = await historyUpdate(messages, country, id, title, appStateContext?.state.ImpersonatedUser, appStateContext?.state.userGId, token, imUserCountry)
            return response
        }
        if (appStateContext && appStateContext.state.currentChat && processMessages === messageStatus.Done) {
            if (appStateContext.state.isSqlDBAvailable.sqlDB) {
                if (!appStateContext?.state.currentChat?.messages) {
                    return
                }
                saveToDB(appStateContext.state.currentChat.messages, appStateContext.state.currentChat.id, appStateContext.state.currentChat.title)
                    .then((res) => {
                        if (!res.ok) {
                            let errorMessage = "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator.";
                            let errorChatMsg: ChatMessage = {
                                id: uuid(),
                                role: ERROR,
                                content: errorMessage,
                                date: new Date().toISOString()
                            }
                            if (!appStateContext?.state.currentChat?.messages) {
                                let err: Error = {
                                    ...new Error,
                                    message: "Failure fetching current chat state."
                                }
                                throw err
                            }
                            setMessages([...appStateContext?.state.currentChat?.messages, errorChatMsg])
                        }
                        return res as Response
                    })
                    .catch((err) => {
                        let errRes: Response = {
                            ...new Response,
                            ok: false,
                            status: 500,
                        }
                        return errRes;
                    })
                appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext.state.currentChat });
            } else { }
            setMessages(appStateContext.state.currentChat.messages)
            setProcessMessages(messageStatus.NotRunning)
        }
    }, [processMessages]);

    // Function: Handle sending the user's prompt/question  
    const handleSendQuestion = (question: string, id?: string) => {
        // Don't send if the question is empty  
        if (!question) {
            return;
        }
        makeApiRequestWithSqlDB(question, id);
        setQuestionInput(""); // Clear input after sending  
    };

    // Effect: Scroll to the bottom when new messages come in or during loading  
    useLayoutEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" })
    }, [showLoadingMessage, processMessages, messages]);

    // Function: Show a citation (reference) in a new tab  
    const onShowCitation = (citation: Citation) => {
        logEvent({ "eventType": "Reference url clicked", "url": citation.url }, country, appStateContext?.state?.userGId, token, imUserCountry);
        if (citation.url) {
            window.open(citation.url, "_blank");
        }
    };

    // Function: Parse citation content from a tool message  
    const parseCitationFromMessage = (message: ChatMessage) => {
        if (message?.role && message?.role === "tool") {
            try {
                const toolMessage = JSON.parse(message.content) as ToolMessageContent;
                return toolMessage.citations;
            }
            catch {
                return [];
            }
        }
        return [];
    }

    const showToasterFileLimit = () => {
        toast.warning(<div style={{
            direction: i18next.language == 'he' || i18next.language == 'ar' ? "rtl" : "ltr",
            textAlign: i18next.language == 'he' || i18next.language == 'ar' ? "right" : "left",
        }}>{t("ticketMenu.fileLimit")}</div>);
    }

    const showToasterRequiredField = () => {
        toast.warning(<div style={{
            direction: isRTL(t("layout.HowCanIHelpYouToday")) ? "rtl" : "ltr",
            textAlign: isRTL(t("layout.HowCanIHelpYouToday")) ? "right" : "left",
        }} >{t("ticketMenu.requiredFields")}</div>);
    }

    // --- Render the Chat UI ---
    return (
        <motion.div
            role="main"
            animate={{ backgroundColor: messages.length > 0 ? "#F7F9F9" : "#EAEFEF" }}
            transition={{ backgroundColor: { duration: 0.5, ease: "easeInOut" } }}
            style={{
                ...stylesCustom.container,
                direction: isRTL(t("layout.HowCanIHelpYouToday")) ? "rtl" : "ltr",
                textAlign: isRTL(t("layout.HowCanIHelpYouToday")) ? "right" : "left",
            }}
        >
            <LayoutGroup>
                <AnimatePresence mode="popLayout">
                    {!messages || messages.length < 1 ? (
                        <motion.div
                            key="welcome"
                            style={stylesCustom.welcomeState}
                            exit={{ opacity: 0, y: -30, transition: { duration: 0.25, ease: "easeIn" } }}
                        >
                            <div style={stylesCustom.welcomeContainer}>
                                <div style={stylesCustom.appInfoContainer}>
                                    <img src={TevaLogo} alt="teva-logo" style={stylesCustom.appLogo} />
                                    <span style={stylesCustom.separator}></span>
                                    <h2 style={stylesCustom.appName}>{appName}</h2>
                                    {appStateContext?.state.frontendSettings?.ui?.env.toLowerCase() !== "prod" && <span>{appStateContext?.state.frontendSettings?.ui?.env}</span>}
                                </div>
                                <span style={stylesCustom.welcomeMessage}>{t("layout.HowCanIHelpYouToday", { userName: appStateContext?.state.isImpersonating ? imUsername : userName ? userName : "", })}</span>
                            </div>
                            <motion.div layoutId="chat-input" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                <SplitOmnibox
                                    value={questionInput}
                                    onChange={setQuestionInput}
                                    onSearch={(question) => handleSendQuestion(question, appStateContext?.state.currentChat?.id)}
                                    onCreateTicket={openTicketDialog}
                                    isAiToolsActive={false}
                                    isWebButtonActive={false}
                                    isModelSelectorActive={false}
                                    isAttachmentButtonActive={false}
                                    isRecordingButtonActive={false}
                                    labels={{
                                        placeholders: { default: t("chat.inputPlaceholder") }, createTicketTitle: t("chat.openTicket"),
                                        sendButtonAriaLabel: t("chat.sendEnabledTitle"), sendButtonTitle: t("chat.sendEnabledTitle"), sendButtonDisabledTitle: t("chat.sendDisabledTitle")
                                    }}
                                    isRtl={isRTL(t("common.learnMore"))}
                                    aiDisclaimerLabels={{
                                        disclaimerText: t("layout.footerLabelShort"),
                                        learnMoreText: t("common.learnMore")
                                    }}
                                    aiDisclaimerOnClick={() => setOpenDisclaimer(true)}
                                    isMentionMenuEnabled={false}
                                    ticketButtonVariant={null}
                                />
                            </motion.div>

                            <motion.div className={styles.ticketSuggestionWrapper} style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                <TicketSuggestion
                                    labels={{
                                        title: t("ticketSuggestion.title"),
                                        description: t("ticketSuggestion.description"),
                                        buttonLabel: t("chat.openTicket"),
                                    }}
                                    dir={isRTL(t("common.learnMore")) ? "rtl" : "ltr"}
                                    onOpen={openTicketDialog}
                                />
                            </motion.div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="chat-active"
                            style={stylesCustom.chatActive}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, transition: { duration: 0.1, ease: "easeIn" } }}
                            transition={{ duration: 0.1 }}
                        >
                            <div
                                style={stylesCustom.chatMessageStream}
                                role="log"
                            >
                                {messages.map((answer, index) => {
                                    // Split out follow-up questions and citations  
                                    let followUpQuestions: string[] = [];
                                    let mainResponse = answer.content;
                                    let uniqueCitations: any[] = [];
                                    if ((answer.role === "assistant" || answer.role === "tool_call") && answer.content) {
                                        let sections;
                                        if (answer.content.includes("### Follow-Up Questions:")) {
                                            sections = answer.content.split("### Follow-Up Questions:");
                                        } else if (answer.content.includes("### שאלות המשך:")) {
                                            sections = answer.content.split("### שאלות המשך:");
                                        } else {
                                            sections = [answer.content];
                                        }
                                        if (sections.length > 1) {
                                            const followUpSection = sections[1].trim();
                                            const lines = followUpSection.split("\n");
                                            followUpQuestions = lines
                                                .filter((line) => line.trim().startsWith("-"))
                                                .map((line) => line.replace("-", "").trim());
                                            mainResponse = sections[0].trim();
                                        }
                                        const parsedCitations = parseCitationFromMessage(messages[index - 1]) || [];
                                        const citationTitles = new Map();
                                        parsedCitations.forEach((citation, originalIndex) => {
                                            const title = citation.title?.trim();
                                            if (title && !citationTitles.has(title)) {
                                                citationTitles.set(title, uniqueCitations.length + 1);
                                                uniqueCitations.push(citation);
                                            }
                                        });
                                        const docPlaceholderRegex = /$doc(\d+)$/g;
                                        mainResponse = mainResponse.replace(docPlaceholderRegex, (match, p1) => {
                                            const originalIndex = parseInt(p1, 10) - 1;
                                            const citation = parsedCitations[originalIndex];
                                            if (citation) {
                                                const newIndex = citationTitles.get(citation.title?.trim());
                                                return newIndex ? `[doc${newIndex}]` : '';
                                            }
                                            return '';
                                        });
                                        // mainResponse = mainResponse.replace(/$doc\d+$/g, (match) => {
                                        //     const docNumber = match.match(/\d+/)?.[0];
                                        //     const originalIndex = docNumber ? parseInt(docNumber, 10) - 1 : 0;
                                        //     if (originalIndex >= parsedCitations.length || !parsedCitations[originalIndex]) {
                                        //         return '';
                                        //     }
                                        //     return match;
                                        // });
                                    }
                                    return (
                                        <Fragment key={`${answer.id}-${index}`}>
                                            {answer.role === "user" && (
                                                <Answer type='user' answer={{
                                                    answer: answer.content,
                                                    citations: [],
                                                    message_id: answer.id ?? '',
                                                    followUpQuestions: [],
                                                    date: answer.date
                                                }} />
                                            )}
                                            {(answer.role === "assistant" || answer.role === "tool_call") && (
                                                <div style={{ ...stylesCustom.chatMessageGptt, alignItems: isRTL(t("chat.generatingAnswer")) ? "flex-end" : "flex-start" }}>
                                                    <Answer
                                                        answer={{
                                                            answer: mainResponse,
                                                            citations: uniqueCitations,
                                                            message_id: answer.id,
                                                            feedback: answer.feedback,
                                                            followUpQuestions,
                                                            date: answer.date
                                                        }}
                                                        onCitationClicked={(c) => onShowCitation(c)}
                                                        onRetry={(userPrompt) => handleRetry(userPrompt)}
                                                        userPrompt={messages[index - 2]?.content}
                                                        onFollowUpClick={(question) => {
                                                            logEvent({ "eventType": "Follow-up question clicked", "question": question }, country, appStateContext?.state?.userGId, token, imUserCountry);
                                                            handleSendQuestion(question, appStateContext?.state.currentChat?.id);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                            {answer.role === ERROR && (
                                                <MessageBubble
                                                    type="error"
                                                    messageContent={{
                                                        id: answer.id ?? '',
                                                        message: answer.content,
                                                        timestamp: parseTimestampToDate(answer.date),
                                                    }}
                                                    onRegenerate={() => handleRetry(messages[index - 1]?.content ?? '')}
                                                />
                                            )}
                                        </Fragment>
                                    );
                                })}
                                {showLoadingMessage && (
                                    <MessageBubble
                                        type="bot"
                                        isLoading={true}
                                        loadingText={t("chat.generatingAnswer")}
                                        disclaimerText={t("answer.aiGeneratedDisclaimer")}
                                        isRTL={isRTL(t("chat.generatingAnswer"))}
                                    />
                                )}
                                <div ref={chatMessageStreamEnd} />
                            </div>
                            <div style={stylesCustom.chatInputWrapper}>
                                <motion.div layoutId="chat-input">
                                    <SplitOmnibox
                                        value={questionInput}
                                        onChange={setQuestionInput}
                                        onSearch={(question) => handleSendQuestion(question, appStateContext?.state.currentChat?.id)}
                                        onCreateTicket={openTicketDialog}
                                        isAiToolsActive={false}
                                        isWebButtonActive={false}
                                        isModelSelectorActive={false}
                                        isAttachmentButtonActive={false}
                                        isRecordingButtonActive={false}
                                        labels={{ placeholders: { default: t("chat.inputPlaceholder") }, createTicketTitle: t("chat.openTicket"), createTicketText: t("chat.openTicket"), sendButtonAriaLabel: t("chat.sendEnabledTitle"), sendButtonTitle: t("chat.sendEnabledTitle"), sendButtonDisabledTitle: t("chat.sendDisabledTitle") }}
                                        isRtl={isRTL(t("common.learnMore"))}
                                        aiDisclaimerLabels={{
                                            disclaimerText: t("layout.footerLabelShort"),
                                            learnMoreText: t("common.learnMore")
                                        }}
                                        aiDisclaimerOnClick={() => setOpenDisclaimer(true)}
                                        isMentionMenuEnabled={false}
                                        ticketButtonVariant={"full"}

                                    />
                                </motion.div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </LayoutGroup>
            {/* Ticket Dialog */}
            <Ticket
                isOpen={isTicketDialogVisible}
                dir={isRTL(t("chat.ticketTitle")) ? 'rtl' : 'ltr'}
                title={ticket.Title}
                description={ticket.Description}
                type={ticketDialogType}
                isLoading={isSubmitting}
                aiDescriptionLoading={isLoading}
                labels={{
                    title: ticketDialogType === 'form' ? t("chat.ticketTitle") : '',
                    subtitle: ticketDialogType === 'form' && appStateContext?.state.currentChat?.messages ? t("ticketMenu.descriptionHelperText") : '',
                    submitLabel: t("chat.submitButton"),
                    titlePlaceholder: t("ticketMenu.titlePlaceholder"),
                    titleLabel: t("ticketMenu.titleLabel"),
                    descriptionPlaceholder: t("ticketMenu.descriptionPlaceholder"),
                    descriptionLabel: t("ticketMenu.descriptionLabel"),
                    attachmentLabel: t("ticketMenu.attachmentLabel"),
                    attachmentPlaceholder: t("ticketMenu.dragDropHint"),
                    attachmentIcon: t("ticketMenu.attachmentIcon"),
                    attachmentTooltip: t("ticketMenu.attachmentIcon"),
                    removeAttachment: t("ticketMenu.removeAttachment"),
                    ticketSuccessMessage: t("ticketMenu.emailSuccess"),
                    ticketErrorMessage: t("ticketMenu.emailFail"),
                    ticketSpinnerText: t("chat.ticketSpinnerText"),
                }}
                onSubmitTicket={saveTicket}
                onClose={closeTicketDialog}
                maxFileSizeReached={showToasterFileLimit}
                fieldRequired={showToasterRequiredField}
                styles={ticketDialogType !== 'form' ?
                    {
                        dialog: {
                            dialog: {
                                main: {
                                    maxHeight: 'min(90dvh, calc(var(--app-height, 100dvh) * 0.90))'
                                }
                            },
                            header: {
                                borderBottom: 'none'
                            }
                        },
                        contentStatus: {
                            alignItems: 'center'
                        }
                    } : {
                        dialog: {
                            dialog: {
                                main: {
                                    maxHeight: 'min(90dvh, calc(var(--app-height, 100dvh) * 0.90))'
                                }
                            }
                        }
                    }
                }
            />

            <Dialog
                isOpen={openDisclaimer}
                onDismiss={() => setOpenDisclaimer(false)}
                styles={{
                    dialog: {
                        main: {
                            maxWidth: 500,
                            maxHeight: 'min(90dvh, calc(var(--app-height, 100dvh) * 0.90))'
                        }
                    },
                    header: {
                        display: 'flex',
                        padding: '10px',
                        flexDirection: isRTL("common.Disclaimer") ? 'row' : 'row-reverse',
                        borderBottom: 'none'
                    }
                }}
                header={
                    <IconButton
                        iconProps={CANCEL_ICON}
                        ariaLabel="Close popup modal"
                        onClick={() => setOpenDisclaimer(false)}
                    />
                }
                content={
                    <div
                        style={
                            {
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                                padding: "0 20px 24px",
                                textAlign: isRTL(t("common.learnMore")) ? 'right' : 'left',
                                direction: isRTL(t("common.learnMore")) ? 'rtl' : 'ltr'
                            }}>
                        <span> {t("common.Disclaimer")}</span>
                        <a href="https://www.myteva.com/page/58263" target="_blank">{t("common.learnMore")}</a>
                    </div>
                }
            />
        </motion.div>
    );
};
export default Chat;  